// SPDX-FileCopyrightText: © 2023 Schneider Electric
//
// SPDX-License-Identifier: Apache-2.0

import type { ComponentInterface, EventEmitter } from "@stencil/core";
import { Component, Element, Event, h, Host, Prop, State } from "@stencil/core";
import type { LiteralUnion } from "type-fest";

import state from "../store";
import { onMobileBreakpoint } from "../utils";

export type Target = "_blank" | "_parent" | "_self" | "_top";

/**
 * @slot start - Content to display in the start slot of the megamenu content.
 * @slot banner - Banner content for the megamenu content.
 */

@Component({
  tag: "qds-web-megamenu-content",
  shadow: true,
  styleUrl: "megamenu-content.css",
})
export class MegamenuContent implements ComponentInterface {
  @Element() public el: HTMLElement;

  /**
   * The text content of the selector.
   */
  @Prop() public readonly selectorText?: string;

  /**
   * The URL link associated with the qds-web-title-link.
   */
  @Prop() public readonly hrefTitle?: string;

  /**
   * Where to display the linked URL, as the name for a browsing context (a
   * tab, window, or
   * [`<iframe>`](https://developer.mozilla.org/docs/Web/HTML/Element/iframe).
   * The following keywords have special meanings for where to load the URL:
   *
   * - `_blank`: usually a new tab, but users can configure browsers to open a new
   *   window instead.
   * - `_parent`: the parent browsing context of the current one. If no parent,
   *   behaves as `_self`.
   * - `_self`: the current browsing context.
   * - `_top`: the topmost browsing context (the "highest" context that's an
   *   ancestor of the current one). If no ancestors, behaves as `_self`.
   *
   * @webnative
   */
  @Prop() public readonly target?: LiteralUnion<Target, string>;

  /**
   * Where to display the linked URL, as the name for a browsing context (a
   * tab, window, or
   * [`<iframe>`](https://developer.mozilla.org/docs/Web/HTML/Element/iframe).
   * The following keywords have special meanings for where to load the URL:
   *
   * - `_blank`: usually a new tab, but users can configure browsers to open a new
   *   window instead.
   * - `_parent`: the parent browsing context of the current one. If no parent,
   *   behaves as `_self`.
   * - `_self`: the current browsing context.
   * - `_top`: the topmost browsing context (the "highest" context that's an
   *   ancestor of the current one). If no ancestors, behaves as `_self`.
   *
   * @webnative
   */
  @Prop() public readonly selectorTarget?: LiteralUnion<Target, string>;

  /**
   * The URL link associated with the selector.
   */
  @Prop() public readonly selectorHref: string;

  /**
   * The itemId value of each megamenu item.
   */
  @Prop() public itemId?: string;

  /**
   * The megamenu content description.
   */
  @Prop() public readonly description?: string;

  /**
   * The text content of qds-web-title-link.
   */
  @Prop() public readonly text: string;

  /**
   * Emitted when the navItem is clicked.
   */
  @Event({ eventName: "qdsClick", bubbles: false, cancelable: false })
  public clickEmitter: EventEmitter<void>;

  /**
   * Emitted when the corresponding megamenu is opened.
   */

  @Event({ eventName: "qds-nav-item:open" })
  public openNavTabs: EventEmitter<boolean>;

  /**
   * Emitted when the megamenu items are closed.
   */
  @Event({ eventName: "qds-megamenu-item:close" })
  public closeMegamenuItems: EventEmitter<boolean>;

  /**
   * Emitted when the nav tabs are closed.
   */
  @Event({ eventName: "qds-nav-item:close" })
  public closeNavTabs: EventEmitter<boolean>;

  @State() public isMobile: boolean;

  public descriptionElement: HTMLElement;

  private cleanupMobileListener?: () => void;

  private mainMenuEl?: HTMLElement;

  public componentDidRender(): void {
    if (state.item === this.itemId) {
      this.setDescriptionHeight();
      requestAnimationFrame(() => {
        if (this.isMobile && this.mainMenuEl) {
          this.mainMenuEl.focus();
        }
      });
    }
  }

  public componentDidLoad(): void {
    this.setAriaLabel();

    const titleLinkElement = this.el
      .closest("qds-web-megamenu")
      ?.querySelector("qds-web-title-link");

    const focusableElements = [
      ...this.el.querySelectorAll<HTMLElement>(
        "qds-web-megamenu-list-item, qds-web-megamenu-content-banner",
      ),
    ] as HTMLElement[];

    const lastElement = focusableElements.at(-1);

    lastElement?.addEventListener("keydown", (event: KeyboardEvent) => {
      if (event.key === "Tab" && !event.shiftKey) {
        event.preventDefault();
        titleLinkElement?.setAttribute("tabindex", "0");
        titleLinkElement?.focus();
        this.el.setAttribute("tabindex", "-1");
        this.el.setAttribute("aria-hidden", "true");
      }
    });
  }

  public componentWillLoad(): Promise<void> | void {
    this.cleanupMobileListener = onMobileBreakpoint((isMobile: boolean) => {
      this.isMobile = isMobile;
    }, this.el);
  }

  public disconnectedCallback(): void {
    this.cleanupMobileListener?.();
  }

  public render() {
    if (state.item !== this.itemId) {
      // eslint-disable-next-line unicorn/no-null
      return null;
    }

    return (
      <Host
        tabIndex={this.isMobile ? undefined : 0}
        role={this.isMobile ? undefined : "tabpanel"}
      >
        <div
          class={{
            web: !this.isMobile,
            mobile: this.isMobile,
            hide: state.item !== this.itemId,
          }}
        >
          <div class="content">
            {this.isMobile && (
              <qds-standalone-link
                importance="subdued"
                size="large"
                icon-name="chevron-left"
                onClick={() => {
                  this.closeMegamenuItems.emit();
                }}
                onKeyDown={(event: KeyboardEvent) => {
                  if (event.key === "Enter") {
                    this.closeMegamenuItems.emit();
                  }
                }}
                ref={(element: HTMLElement) => {
                  this.mainMenuEl = element;
                }}
              >
                {state.tab}
              </qds-standalone-link>
            )}
            <div class="header">
              {!this.isMobile && (
                <qds-button
                  icon-only
                  text="Close menu"
                  data-role="close"
                  icon-name="cross-large"
                  aria-label="Close menu"
                  importance="subdued"
                  onClick={() => {
                    this.closeNavTabs.emit(true);
                    this.openNavTabs.emit(false);
                  }}
                />
              )}

              <div
                class={{
                  "header-content": true,
                  "selector-gap":
                    this.selectorText !== undefined && this.selectorText !== "",
                }}
              >
                {this.selectorText !== undefined &&
                  this.selectorText !== "" && (
                    <div>
                      <qds-button
                        text={this.selectorText}
                        href={this.selectorHref}
                        target={this.selectorTarget}
                        size="hero"
                        action="next"
                        icon-name="input-url"
                        icon-library="core"
                        onClick={() => {
                          if (this.selectorTarget !== "_blank") {
                            this.closeNavTabs.emit(true);
                            this.openNavTabs.emit(false);
                          }
                        }}
                      />
                    </div>
                  )}

                <div class="start">
                  <slot name="start" />
                  <div class="title-content">
                    <qds-web-title-link
                      id={`${this.itemId}-title-link`}
                      tag={
                        this.hrefTitle !== undefined && this.hrefTitle !== ""
                          ? "h2"
                          : undefined
                      }
                      href={
                        this.hrefTitle !== undefined && this.hrefTitle !== ""
                          ? this.hrefTitle
                          : undefined
                      }
                      text={this.text}
                      target={this.target}
                      icon-library="core"
                      onClick={() => {
                        this.clickEmitter.emit();
                      }}
                      icon-name="next"
                      size="large"
                    />
                    <p
                      ref={(element) => {
                        this.descriptionElement = element as HTMLElement;
                      }}
                      class="description"
                    >
                      {this.description}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div>
            <slot />
          </div>
          <slot name="banner" />
        </div>
      </Host>
    );
  }

  private setDescriptionHeight(): void {
    const descriptionHeight = this.descriptionElement.clientHeight;
    if (descriptionHeight >= 19 && descriptionHeight <= 58) {
      document.documentElement.style.setProperty(
        "--description-height",
        "-55px",
      );
    } else {
      document.documentElement.style.setProperty(
        "--description-height",
        "-37px",
      );
    }
  }

  private setAriaLabel(): void {
    const itemComponent = this.el
      .closest("qds-web-megamenu")
      ?.querySelector(`qds-web-megamenu-item[id="${this.itemId}"]`);

    const itemLabel = itemComponent?.getAttribute("label") ?? "";
    this.el.setAttribute("aria-label", itemLabel);
  }
}
